if (!customElements.get('rs-cart-stale-check')) {
  const LEGACY_MTO_KEY = 'Made to Order';
  const LEGACY_REMOVE_KEYS = ['_return_type_message', '_Return Code'];
  const LEGACY_LIP_KEY = /^lip_(\d+)_customization$/;

  customElements.define('rs-cart-stale-check', class extends HTMLElement {
    connectedCallback() {
      this._dialog = this.querySelector('[data-stale-dialog]');
      if (!this._dialog) return;

      this._proceedBtn = this._dialog.querySelector('[data-stale-proceed]');
      this._cancelBtn  = this._dialog.querySelector('[data-stale-cancel]');

      // MTO shipping-note template
      this._mtoNoteTemplate = this.dataset.mtoShippingNote || '';

      // Always check the live cart
      this._onSubmit = (e) => {
        if (e.target?.id !== 'cartForm') return;
        if (e.submitter?.name !== 'checkout') return;
        e.preventDefault();
        this._onCheckout(e.submitter);
      };
      document.addEventListener('submit', this._onSubmit, true);

      this._cancelBtn?.addEventListener('click', () => {
        this._dialog.close();
        this._checkoutBtn.disabled = false;
      });

      this._proceedBtn?.addEventListener('click', () => this._applyAndProceed());

      // Stale cart check on page load
      if (this.dataset.hasStale === 'true') this._runSilentPass();
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this._onSubmit, true);
    }

    async _runSilentPass() {
      if (window.__rsStaleSilentDone || !window.RSStorefrontApiConfig) return;
      window.__rsStaleSilentDone = true;
      try {
        const { affected, updates } = await this._collectUpdates();
        if (updates.some(Boolean)) await this._applyUpdates(affected, updates);
      } catch (_e) {}
    }

    async _onCheckout(btn) {
      this._checkoutBtn = btn;
      btn.disabled = true;
      try {
        if (!window.RSStorefrontApiConfig) return this._proceed();

        const { affected, updates } = await this._collectUpdates();
        if (!updates.some(Boolean)) return this._proceed();

        this._pendingAffected = affected;
        this._pendingUpdates  = updates;

        // Modal only for a changed ship date; silent metadata fixes just apply.
        if (updates.some(u => u && u.userFacing)) {
          btn.disabled = false;
          this._dialog.showModal();
        } else {
          await this._applyUpdates(affected, updates);
          this._proceed();
        }
      } catch (_e) {
        this._proceed(btn); // never block checkout on error
      }
    }

    async _applyAndProceed() {
      this._proceedBtn.disabled = true;
      this._proceedBtn.textContent = 'Updating…';
      await this._applyUpdates(this._pendingAffected, this._pendingUpdates);
      this._proceed();
    }

    _proceed(btn) {
      if (btn) btn.disabled = false;
      window.location.href = '/checkout';
    }

    // A line needs a refresh if it carries a date/legacy key or lacks _LPROP
    _needsUpdate(item) {
      const p = item.properties || {};
      return p['Made To Order'] ||
        p[LEGACY_MTO_KEY] ||
        p['Date Available'] ||
        LEGACY_REMOVE_KEYS.some(k => p[k] !== undefined) ||
        Object.keys(p).some(k => LEGACY_LIP_KEY.test(k)) ||
        p['_LPROP'] === undefined;
    }

    // Skip updating Builder/bundle (LinesMerge) lines
    _isBundleItem(item) {
      const p = item.properties || {};
      return p['_ref'] !== undefined ||
        (Array.isArray(item.item_components) && item.item_components.length > 0);
    }

    // Fetch the cart, then compute the property diff for each stale (non-builder lines)
    // Returns parallel arrays; updates[i] is null when line i needs no change.
    async _collectUpdates() {
      const cart = await fetch('/cart.js').then(r => r.json());
      const affected = cart.items.filter(item => !this._isBundleItem(item) && this._needsUpdate(item));
      if (!affected.length) return { affected: [], updates: [] };
      const updates = await this._fetchUpdates(affected);
      return { affected, updates };
    }

    // Query the Storefront API for each line's live pre-order / MTO dates.
    async _fetchUpdates(affected) {
      const config = window.RSStorefrontApiConfig;
      const query = affected.map((item, i) => `
        v${i}: node(id: "gid://shopify/ProductVariant/${item.variant_id}") {
          ... on ProductVariant {
            preorderDate: metafield(namespace: "custom", key: "preorder_availability_date") { value }
            mtoSltDate: metafield(namespace: "custom", key: "mto_production_timeline_slt") { value }
            product {
              preorderDateProd: metafield(namespace: "custom", key: "preorder_availability_date") { value }
              mtoSltDateProd: metafield(namespace: "custom", key: "mto_production_timeline_date_slt") { value }
            }
          }
        }`).join('');

      const res = await fetch(`https://${config.domain}/api/${config.apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': config.token
        },
        body: JSON.stringify({ query: `{ ${query} }` })
      });

      const { data } = await res.json();
      if (!data) return affected.map(() => null);
      return affected.map((item, i) => this._computeChangedProps(data[`v${i}`], item));
    }

    // Diff a line's stored properties against its live dates.
    // Returns { set, remove, userFacing } or null when nothing changed.
    _computeChangedProps(variantData, item) {
      if (!variantData) return null;

      const p    = item.properties || {};
      const prod = variantData.product || {};
      const preorderDate = variantData.preorderDate || prod.preorderDateProd;
      const mtoSltDate   = variantData.mtoSltDate   || prod.mtoSltDateProd;

      const returnTypeCode = document.querySelector(`[data-item="${item.key}"]`)?.getAttribute('data-item-type') || 'standard_product';

      const fresh = {};
      const remove = [];
      let userFacing = false;

      // Pre-order
      if (p['Date Available'] && preorderDate?.value) {
        const longDate = this._formatLong(preorderDate.value);
        const dateAvailable = `Pre-Order: Item will ship by ${longDate}. Note that item may ship separately from others in your order.`;
        if (p['Date Available'] !== dateAvailable) userFacing = true;
        fresh['Date Available'] = dateAvailable;
        fresh['_preorder_availability_date'] = longDate;
      }

      // Made To Order (future dates only). Legacy 'Made to Order' migrates to 'Made To Order'.
      const hasLegacyMto = p[LEGACY_MTO_KEY] !== undefined;
      if ((p['Made To Order'] || hasLegacyMto) && mtoSltDate?.value) {
        const iso = this._parseMtoIso(mtoSltDate.value);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        if (new Date(iso + 'T00:00:00') > today) {
          const timeline = `Ships on ${this._formatShort(iso)}`;
          if ((p['Made To Order'] || p[LEGACY_MTO_KEY]) !== timeline) userFacing = true;
          fresh['Made To Order']  = timeline;
          fresh['_Made to Order'] = timeline.replace(/\//g, '-');

          // Refresh the note under the key the line already uses — never add a new key (avoids a duplicate note).
          const noteKey = p['_Shipping Note'] !== undefined ? '_Shipping Note'
            : p['Shipping Note'] !== undefined ? 'Shipping Note'
            : null;
          if (noteKey && this._mtoNoteTemplate) {
            fresh[noteKey] = this._mtoNoteTemplate.split('||DATE||').join(this._formatShort(iso));
          }
          if (hasLegacyMto) remove.push(LEGACY_MTO_KEY);
        }
      }

      if (p['_LPROP'] === undefined) fresh['_LPROP'] = returnTypeCode; // backfill old-theme lines
      LEGACY_REMOVE_KEYS.forEach(k => { if (p[k] !== undefined) remove.push(k); });

      // Retire the legacy public lip_{n}_customization in favour of private _Customization_{n}
      Object.entries(p).forEach(([key, value]) => {
        const legacyLip = key.match(LEGACY_LIP_KEY);
        if (!legacyLip) return;
        if (value) fresh['_Customization_' + legacyLip[1]] = value;
        remove.push(key);
      });

      // Keep only genuinely-changed keys.
      const set = {};
      for (const [k, v] of Object.entries(fresh)) if (p[k] !== v) set[k] = v;

      if (!Object.keys(set).length && !remove.length) return null;
      return { set, remove, userFacing };
    }

    async _applyUpdates(affected, updates) {
      try {
        for (let i = 0; i < affected.length; i++) {
          if (!updates[i]) continue;
          const item = affected[i];
          const properties = { ...item.properties, ...updates[i].set };
          updates[i].remove.forEach(k => delete properties[k]);

          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: item.quantity, properties })
          });
        }
      } catch (_e) {}
    }

    _parseMtoIso(sltDate) {
      const [mm, dd, yyyy] = sltDate.split('-');
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }

    _formatShort(iso) {
      const d = new Date(iso + 'T00:00:00');
      return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
    }

    _formatLong(iso) {
      const d = new Date(iso + 'T00:00:00');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
    }
  });
}
